/**
 * Conditions sub-wizard (Tailwind). A leaf is a fact-check or history-check;
 * leaves combine into ALL/ANY groups. rule_type is derived and shown.
 */
import React from "react";
import { MdDelete, MdAdd } from "react-icons/md";
import { FiFilter, FiGitMerge, FiTarget, FiInfo, FiLayers } from "react-icons/fi";
import { FactCheckForm, HistoricalForm } from "./conditionEditors";
import { emptyFactCheck, emptyHistorical, emptyGroup, deriveRuleType } from "./ruleModel";
import { Card, Chip, Btn, Segmented, IconBtn, Select, cx } from "./ui";

const isGroup = (c) => c && (c.type === "AND" || c.type === "OR");

function LeafCard({ node, path, errors, onChange, onRemove }) {
  const kind = node.type === "historical_aggregate" ? "historical" : "fact";
  const setKind = (k) => onChange(k === "historical" ? emptyHistorical() : emptyFactCheck());

  return (
    <Card className="border-l-4 border-l-blue-500 p-4 shadow-xs">
      <div className="flex items-center justify-between mb-3 pb-1 border-b border-gray-100">
        <div className="w-72">
          <Select
            value={kind}
            onChange={setKind}
            options={[
              { value: "fact", label: "Event Fact Check (current transaction)" },
              { value: "historical", label: "User History Check (cumulative/streak)" },
            ]}
          />
        </div>
        {onRemove && <IconBtn onClick={onRemove} title="Delete condition"><MdDelete className="h-4 w-4" /></IconBtn>}
      </div>
      {kind === "fact"
        ? <FactCheckForm condition={node} path={path} errors={errors} onChange={onChange} />
        : <HistoricalForm condition={node} path={path} errors={errors} onChange={onChange} />}
    </Card>
  );
}

function GroupCard({ node, path, errors, onChange, onRemove, depth }) {
  const setType = (t) => onChange({ ...node, type: t });
  const setChild = (i, child) => onChange({ ...node, children: node.children.map((c, idx) => (idx === i ? child : c)) });
  const removeChild = (i) => onChange({ ...node, children: node.children.filter((_, idx) => idx !== i) });
  const addLeaf = () => onChange({ ...node, children: [...node.children, emptyFactCheck()] });
  const addGroup = () => onChange({ ...node, children: [...node.children, emptyGroup(node.type === "AND" ? "OR" : "AND")] });

  return (
    <div className={cx(
      "rounded-2xl border-l-4 bg-gray-50/70 border border-gray-200 p-4 shadow-2xs space-y-3",
      depth % 2 ? "border-l-indigo-500 bg-indigo-50/20" : "border-l-blue-600 bg-blue-50/20"
    )}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Segmented value={node.type} onChange={setType} options={[{ value: "AND", label: "ALL of" }, { value: "OR", label: "ANY of" }]} />
          <span className="text-xs text-gray-500 font-medium">Group Criteria</span>
        </div>
        {onRemove && <IconBtn onClick={onRemove} title="Delete group"><MdDelete className="h-4 w-4" /></IconBtn>}
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-white/80 p-2 rounded-lg border border-gray-200/60">
        <FiInfo className="h-3.5 w-3.5 text-blue-500 shrink-0" />
        <span>
          {node.type === "AND"
            ? "All conditions below must evaluate to TRUE — any false fact-check immediately blocks rule execution."
            : "Satisfying ANY single condition below is sufficient to pass this evaluation group."}
        </span>
      </div>

      <div className="grid gap-3">
        {(node.children || []).map((child, i) => (
          <ConditionNode
            key={i}
            node={child}
            path={`${path}.children[${i}]`}
            errors={errors}
            depth={depth + 1}
            onChange={(c) => setChild(i, c)}
            onRemove={() => removeChild(i)}
          />
        ))}
      </div>

      <div className="pt-2 flex items-center gap-2 flex-wrap">
        <Btn variant="soft" onClick={addLeaf}><MdAdd /> Add Condition</Btn>
        {depth < 1 && <Btn variant="soft" onClick={addGroup}><MdAdd /> Add Nested Group</Btn>}
      </div>
    </div>
  );
}

function ConditionNode(props) {
  return isGroup(props.node) ? <GroupCard {...props} /> : <LeafCard {...props} />;
}

export default function StepConditions({ draft, errors, onChange }) {
  const condition = draft.condition;
  const startSingle = () => onChange(emptyFactCheck());
  const startGroup = () => onChange({ ...emptyGroup("AND"), children: [emptyFactCheck()] });
  const wrapInGroup = () => onChange({ ...emptyGroup("AND"), children: [condition] });

  if (!condition) {
    return (
      <div className="space-y-4 py-4">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Define Rule Evaluation Criteria</h3>
          <p className="text-xs text-gray-500">Choose how many conditions are required to validate this rule when triggered.</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <button
            type="button"
            onClick={startSingle}
            className="text-left p-5 rounded-2xl border border-gray-200 bg-white hover:border-blue-500 hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                <FiFilter className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-900">Single Condition</h4>
                <span className="text-xs text-gray-500">Direct fact check or history metric</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">Evaluate one single field or historical threshold (e.g., scan count {'>'} 5 or user state = UP).</p>
          </button>

          <button
            type="button"
            onClick={startGroup}
            className="text-left p-5 rounded-2xl border border-gray-200 bg-white hover:border-blue-500 hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                <FiGitMerge className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-900">Multiple Conditions</h4>
                <span className="text-xs text-gray-500">Combine with ALL of or ANY of logic</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">Build complex logic combining multiple fact checks and historical milestones in logical groups.</p>
          </button>
        </div>
      </div>
    );
  }

  const derivedType = deriveRuleType(condition);

  return (
    <div className="space-y-4">
      {/* Rule Strategy Banner */}
      <div className="flex items-center justify-between bg-gradient-to-r from-indigo-50/80 via-blue-50/40 to-indigo-50/80 rounded-2xl p-4 border border-indigo-100 shadow-2xs">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-indigo-600 text-white shadow-xs">
            <FiTarget className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-900">Derived Rule Strategy</h4>
              <Chip color="indigo">{derivedType}</Chip>
            </div>
            <p className="text-xs text-gray-600 mt-0.5">
              {derivedType === "scan_gate" && "Strict gate: Fact checks run immediately before history checks."}
              {derivedType === "scan_milestone" && "Milestone rule: Tracks aggregate scan counts across historical activity."}
              {derivedType === "streak" && "Streak rule: Evaluates active daily scan frequency."}
              {derivedType === "custom" && "Custom rule combining fact checks and history aggregations."}
            </p>
          </div>
        </div>
      </div>

      {/* Condition Tree Node */}
      <ConditionNode node={condition} path="condition" errors={errors} depth={0} onChange={onChange} onRemove={() => onChange(null)} />

      {!isGroup(condition) && (
        <div className="pt-2 border-t border-gray-100">
          <Btn variant="soft" onClick={wrapInGroup}><MdAdd /> Wrap into Logical Group (ALL of / ANY of)</Btn>
        </div>
      )}
    </div>
  );
}

